import React, { useEffect, useState, useCallback, useRef } from 'react';
import { statementAPI, accountAPI, expenseAPI } from '../lib/api';
import { formatINR, todayISO, apiError } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import DatePicker from '../components/DatePicker';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Upload,
  Plus,
  Trash2,
  Edit2,
  FileText,
  Save,
  ArrowLeft,
  Loader2,
  Repeat,
  Search,
} from 'lucide-react';

const CATEGORIES = [
  'Rent',
  'Utilities',
  'Software',
  'Salary',
  'Travel',
  'Equipment',
  'Marketing',
  'Insurance',
  'Legal',
  'Director Pay',
  'EMI',
  'Investment',
  'Recharge',
  'Grocery',
  'AI Tools',
  'Other',
  'Uncategorised',
];

const EMPTY_ROW = () => ({
  payment_date: '',
  vendor: '',
  category: 'Uncategorised',
  amount: '',
  description: '',
});

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export default function Expenses() {
  const { can } = useAuth();
  const canWrite = can('write');
  const canDelete = can('delete');
  const [accounts, setAccounts] = useState([]);
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);

  // Upload form
  const [uploadAccountId, setUploadAccountId] = useState('');
  const [uploadPeriod, setUploadPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Editing mode (after upload)
  const [activeStatement, setActiveStatement] = useState(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const [rows, setRows] = useState([EMPTY_ROW()]);
  // Save All replaces the statement's saved expenses, so it stays disabled until
  // those rows have actually loaded (a failed load must never save over them).
  const [rowsLoaded, setRowsLoaded] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const openIdRef = useRef(null);
  const [saving, setSaving] = useState(false);

  // Manual "Add expense" modal
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [manualForm, setManualForm] = useState({
    payment_date: '',
    vendor: '',
    category: 'Uncategorised',
    amount: '',
    description: '',
    account_id: '',
    is_recurring: false,
    frequency: '',
  });

  // Recurring expenses list
  const [recurring, setRecurring] = useState([]);
  const [oneOffs, setOneOffs] = useState([]);

  // Search + pagination for recurring table
  const [recurringSearch, setRecurringSearch] = useState('');
  const [recurringPage, setRecurringPage] = useState(1);

  // Search + pagination for one-off table
  const [oneOffSearch, setOneOffSearch] = useState('');
  const [oneOffPage, setOneOffPage] = useState(1);

  const PAGE_SIZE = 50;

  const fetchData = useCallback(async () => {
    try {
      const [accRes, stmtRes, expRes] = await Promise.all([
        accountAPI.getAll(),
        statementAPI.getAll(),
        expenseAPI.getAll(),
      ]);
      setAccounts(accRes.data);
      setStatements(stmtRes.data);
      setRecurring(expRes.data.filter((e) => e.is_recurring));
      // Statement-imported rows live under their statement, not here.
      setOneOffs(expRes.data.filter((e) => !e.is_recurring && !e.statement_id));
      const firstOpen = accRes.data.find((a) => a.status !== 'closed');
      setUploadAccountId((cur) => cur || firstOpen?.id || '');
    } catch (err) {
      toast.error(apiError(err, 'Failed to load expenses'));
    } finally {
      setLoading(false);
    }
  }, []);

  // Closed accounts are hidden from every picker.
  const openAccounts = accounts.filter((a) => a.status !== 'closed');

  const openManual = () => {
    setEditingExpense(null);
    setManualForm({
      payment_date: todayISO(),
      vendor: '',
      category: 'Uncategorised',
      amount: '',
      description: '',
      account_id: openAccounts[0]?.id || '',
      is_recurring: false,
      frequency: '',
    });
    setManualOpen(true);
  };

  // Accountants can't delete, so they correct mistakes here instead.
  const openEditExpense = (e) => {
    setEditingExpense(e);
    setManualForm({
      payment_date: e.payment_date || '',
      vendor: e.vendor || '',
      category: e.category || 'Uncategorised',
      amount: String(e.amount ?? ''),
      description: e.description || '',
      account_id: e.account_id || '',
      is_recurring: !!e.is_recurring,
      frequency: e.frequency || '',
    });
    setManualOpen(true);
  };

  const submitManual = async (e) => {
    e.preventDefault();
    const amt = parseFloat(manualForm.amount);
    // Older imported expenses may have no account; editing them must not force one.
    if (!manualForm.account_id && !editingExpense) return toast.error('Pick an account');
    if (!amt || amt <= 0) return toast.error('Amount must be > 0');
    setManualSaving(true);
    const acc = accounts.find((a) => a.id === manualForm.account_id);
    const data = {
      category: manualForm.category || 'Uncategorised',
      vendor: manualForm.vendor,
      description: manualForm.description,
      amount: amt,
      payment_date: manualForm.payment_date,
      payment_method: acc?.name || editingExpense?.payment_method || 'Bank',
      account_id: manualForm.account_id || null,
      is_recurring: manualForm.is_recurring,
      frequency: manualForm.is_recurring ? (manualForm.frequency || 'Monthly') : '',
    };
    try {
      if (editingExpense) {
        await expenseAPI.update(editingExpense.id, data);
        toast.success('Expense updated');
      } else {
        await expenseAPI.create({ ...data, due_date: '' });
        toast.success(`Expense added (${formatINR(amt)})`);
      }
      setManualOpen(false);
      fetchData();
    } catch (err) {
      toast.error(apiError(err, 'Failed to save expense'));
    } finally {
      setManualSaving(false);
    }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await expenseAPI.delete(id);
      toast.success('Expense deleted');
      fetchData();
    } catch (err) {
      toast.error(apiError(err, 'Delete failed'));
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      toast.error('Choose a PDF first');
      return;
    }
    if (!uploadAccountId) {
      toast.error('Pick an account');
      return;
    }
    setUploading(true);
    try {
      const res = await statementAPI.upload(
        uploadAccountId,
        uploadPeriod,
        '',
        uploadFile
      );
      toast.success('Statement uploaded');
      await openStatement(res.data);
      setUploadFile(null);
      fetchData();
    } catch (err) {
      toast.error(apiError(err, 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const openStatement = async (statement) => {
    openIdRef.current = statement.id;
    // A slow response from a previously opened statement must not land here.
    const stale = () => openIdRef.current !== statement.id;
    setActiveStatement(statement);
    setPdfUrl('');
    setPdfError(false);
    setRows([EMPTY_ROW()]);
    setRowsLoaded(false);
    // PDF and saved rows load independently: a missing PDF no longer hides the rows.
    statementAPI.pdfBlobUrl(statement.id)
      .then((url) => (stale() ? URL.revokeObjectURL(url) : setPdfUrl(url)))
      .catch(() => { if (!stale()) setPdfError(true); });
    try {
      const exp = await statementAPI.listExpenses(statement.id);
      if (stale()) return;
      if (exp.data && exp.data.length > 0) {
        setRows(
          exp.data.map((e) => ({
            payment_date: e.payment_date || '',
            vendor: e.vendor || '',
            category: e.category || 'Uncategorised',
            amount: String(e.amount || ''),
            description: e.description || '',
            saved: true, // already on the server: only an admin may remove or zero it
          }))
        );
      }
      setRowsLoaded(true);
    } catch (err) {
      if (!stale()) toast.error(apiError(err, "Could not load this statement's saved rows"));
    }
  };

  const closeStatement = () => {
    openIdRef.current = null;
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setActiveStatement(null);
    setPdfUrl('');
    setRows([EMPTY_ROW()]);
  };

  const updateRow = (idx, field, value) => {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const addRow = () => setRows((rs) => [...rs, EMPTY_ROW()]);
  const removeRow = (idx) => setRows((rs) => rs.filter((_, i) => i !== idx));

  const saveAll = async () => {
    if (!rowsLoaded) return;
    if (!canDelete && rows.some((r) => r.saved && !(parseFloat(r.amount) > 0))) {
      toast.error('Saved rows need an amount above 0. Only an admin can remove saved expense rows.');
      return;
    }
    const cleanRows = rows
      .map(({ saved, ...r }) => ({
        ...r,
        amount: parseFloat(r.amount) || 0,
      }))
      .filter((r) => r.amount > 0);
    if (cleanRows.length === 0) {
      toast.error('Add at least one row with an amount > 0');
      return;
    }
    setSaving(true);
    try {
      const res = await statementAPI.saveExpenses(activeStatement.id, cleanRows, true);
      toast.success(`Saved ${res.data.inserted} expenses (${formatINR(res.data.total_amount)})`);
      // Rows just written are now server rows too.
      setRows((rs) => rs.map((r) => (parseFloat(r.amount) > 0 ? { ...r, saved: true } : r)));
      fetchData();
    } catch (err) {
      toast.error(apiError(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const deleteStatement = async (id) => {
    if (!window.confirm('Delete this statement AND all its expenses?')) return;
    try {
      await statementAPI.delete(id);
      toast.success('Statement deleted');
      if (activeStatement?.id === id) closeStatement();
      fetchData();
    } catch (err) {
      toast.error(apiError(err, 'Delete failed'));
    }
  };

  const totalForRows = rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

  // Derived: recurring search + pagination
  const filteredRecurring = recurring.filter((e) => {
    const q = recurringSearch.toLowerCase();
    return (
      !q ||
      (e.category || '').toLowerCase().includes(q) ||
      (e.vendor || '').toLowerCase().includes(q) ||
      (e.description || '').toLowerCase().includes(q)
    );
  });
  const recurringTotalPages = Math.max(1, Math.ceil(filteredRecurring.length / PAGE_SIZE));
  const pagedRecurring = filteredRecurring.slice(
    (recurringPage - 1) * PAGE_SIZE,
    recurringPage * PAGE_SIZE,
  );

  // Derived: one-off search + pagination
  const filteredOneOffs = oneOffs.filter((e) => {
    const q = oneOffSearch.toLowerCase();
    return (
      !q ||
      (e.category || '').toLowerCase().includes(q) ||
      (e.vendor || '').toLowerCase().includes(q) ||
      (e.description || '').toLowerCase().includes(q)
    );
  });
  const oneOffTotalPages = Math.max(1, Math.ceil(filteredOneOffs.length / PAGE_SIZE));
  const pagedOneOffs = filteredOneOffs.slice(
    (oneOffPage - 1) * PAGE_SIZE,
    oneOffPage * PAGE_SIZE,
  );

  // Deleting the last row on the last page would otherwise strand us on an empty page.
  useEffect(() => {
    if (recurringPage > recurringTotalPages) setRecurringPage(recurringTotalPages);
  }, [recurringPage, recurringTotalPages]);
  useEffect(() => {
    if (oneOffPage > oneOffTotalPages) setOneOffPage(oneOffTotalPages);
  }, [oneOffPage, oneOffTotalPages]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // ====== Editor view ======
  if (activeStatement) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={closeStatement}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold font-['Manrope']">
                {activeStatement.account_name} &middot; {activeStatement.period || 'No period'}
              </h1>
              <p className="text-sm text-gray-500">
                Original: {activeStatement.original_filename}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs text-gray-500">Rows total</p>
              <p className="font-mono text-lg font-bold">{formatINR(totalForRows)}</p>
            </div>
            {canWrite && (
              <Button
                onClick={saveAll}
                disabled={saving || !rowsLoaded}
                title={rowsLoaded ? undefined : "Saved rows haven't loaded yet"}
                className="bg-black hover:bg-black/90"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                Save All
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 min-h-[70vh]">
          {/* PDF preview */}
          <Card className="overflow-hidden">
            <CardContent className="p-0 h-[70vh]">
              {pdfUrl ? (
                <iframe
                  title="statement PDF"
                  src={pdfUrl}
                  className="w-full h-full border-0"
                />
              ) : pdfError ? (
                <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                  Could not load the statement PDF.
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Editable grid */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base">Transactions ({rows.length})</CardTitle>
              {canWrite && rowsLoaded && (
                <Button variant="outline" size="sm" onClick={addRow}>
                  <Plus className="w-4 h-4 mr-1" /> Add row
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-0 overflow-auto max-h-[64vh]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 border-b">
                  <tr className="text-left">
                    <th className="py-2 px-2 font-semibold w-[110px]">Date</th>
                    <th className="py-2 px-2 font-semibold">Vendor</th>
                    <th className="py-2 px-2 font-semibold w-[130px]">Category</th>
                    <th className="py-2 px-2 font-semibold text-right w-[100px]">Amount</th>
                    <th className="py-2 px-2 font-semibold">Note</th>
                    <th className="py-2 px-2 w-[40px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-b last:border-b-0">
                      <td className="py-1 px-2">
                        <DatePicker
                          value={r.payment_date}
                          onChange={(v) => updateRow(i, 'payment_date', v)}
                          disabled={!canWrite || !rowsLoaded}
                          className="h-8 text-xs px-2"
                        />
                      </td>
                      <td className="py-1 px-2">
                        <Input
                          value={r.vendor}
                          onChange={(e) => updateRow(i, 'vendor', e.target.value)}
                          disabled={!canWrite || !rowsLoaded}
                          className="h-8 text-xs"
                          placeholder="e.g. Swiggy"
                        />
                      </td>
                      <td className="py-1 px-2">
                        <Select value={r.category} onValueChange={(v) => updateRow(i, 'category', v)} disabled={!canWrite || !rowsLoaded}>
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CATEGORIES.map((c) => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-1 px-2">
                        <Input
                          type="number"
                          value={r.amount}
                          onChange={(e) => updateRow(i, 'amount', e.target.value)}
                          disabled={!canWrite || !rowsLoaded}
                          className="h-8 text-xs text-right font-mono"
                          placeholder="0"
                        />
                      </td>
                      <td className="py-1 px-2">
                        <Input
                          value={r.description}
                          onChange={(e) => updateRow(i, 'description', e.target.value)}
                          disabled={!canWrite || !rowsLoaded}
                          className="h-8 text-xs"
                        />
                      </td>
                      <td className="py-1 px-2 text-right">
                        {canWrite && rowsLoaded && rows.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-red-700"
                            onClick={() => removeRow(i)}
                            disabled={r.saved && !canDelete}
                            title={r.saved && !canDelete ? 'Only an admin can remove saved rows' : undefined}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ====== List + upload view ======
  const recurringTotal = recurring.reduce((s, e) => s + (e.amount || 0), 0);
  const oneOffTotal = oneOffs.reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <div className="space-y-8 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Expenses</h1>
          <p className="text-gray-500 mt-1">
            Add one-off expenses, manage recurring monthly bills, or upload statements in bulk.
          </p>
        </div>
        {canWrite && (
          <Button onClick={openManual} className="bg-black hover:bg-black/90">
            <Plus className="w-4 h-4 mr-1" /> Add expense
          </Button>
        )}
      </div>

      {/* Upload card */}
      {canWrite && (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Upload className="w-5 h-5" /> Upload statement
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpload} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div className="space-y-2 md:col-span-1">
              <Label>Account</Label>
              <Select value={uploadAccountId} onValueChange={setUploadAccountId}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick a bank or card" />
                </SelectTrigger>
                <SelectContent>
                  {openAccounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-1">
              <Label htmlFor="upload-period">Period</Label>
              <Input
                id="upload-period"
                type="month"
                value={uploadPeriod}
                onChange={(e) => setUploadPeriod(e.target.value)}
              />
            </div>
            <div className="space-y-2 md:col-span-1">
              <Label htmlFor="upload-pdf">PDF</Label>
              <Input
                id="upload-pdf"
                type="file"
                accept="application/pdf"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              />
            </div>
            <div className="md:col-span-1">
              <Button
                type="submit"
                disabled={uploading || !uploadFile || !uploadAccountId}
                className="w-full bg-black hover:bg-black/90"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Upload & enter rows'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      )}

      {/* Recurring expenses */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Repeat className="w-5 h-5" /> Recurring monthly expenses
            <span className="ml-auto text-sm font-normal text-gray-500">
              {recurring.length} items · {formatINR(recurringTotal)}/month
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {recurring.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">No recurring expenses yet.</div>
          ) : (
            <>
              <div className="px-4 pt-3 pb-2">
                <div className="relative sm:w-80">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <Input
                    placeholder="Search recurring expenses…"
                    value={recurringSearch}
                    onChange={(e) => { setRecurringSearch(e.target.value); setRecurringPage(1); }}
                    className="pl-8"
                  />
                </div>
              </div>
              <div className="overflow-x-auto"><table className="w-full text-sm min-w-[720px]">
                <thead className="border-b">
                  <tr className="text-left text-gray-500">
                    <th className="py-2 px-4 font-medium w-[70px]">Day</th>
                    <th className="py-2 px-4 font-medium">Vendor</th>
                    <th className="py-2 px-4 font-medium">Description</th>
                    <th className="py-2 px-4 font-medium">Category</th>
                    <th className="py-2 px-4 font-medium">Account</th>
                    <th className="py-2 px-4 font-medium text-right">Amount</th>
                    <th className="py-2 px-4 w-[80px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRecurring.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-gray-400 text-sm">
                        No recurring expenses match your search.
                      </td>
                    </tr>
                  ) : (
                    pagedRecurring.map((e) => (
                      <tr key={e.id} className="border-b last:border-b-0 hover:bg-gray-50">
                        <td className="py-2 px-4 font-mono">{e.payment_date || '—'}</td>
                        <td className="py-2 px-4 font-medium">{e.vendor}</td>
                        <td className="py-2 px-4 text-gray-500 text-xs">{e.description}</td>
                        <td className="py-2 px-4 text-gray-600">{e.category}</td>
                        <td className="py-2 px-4 text-gray-500 text-xs">{e.payment_method}</td>
                        <td className="py-2 px-4 text-right font-mono">{formatINR(e.amount)}</td>
                        <td className="py-2 px-4 whitespace-nowrap">
                          {canWrite && (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEditExpense(e)} title="Edit">
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-700" onClick={() => deleteExpense(e.id)} title="Delete">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table></div>
              {filteredRecurring.length > PAGE_SIZE && (
                <div className="flex items-center justify-between px-4 py-3 border-t text-sm text-gray-500">
                  <span>
                    Showing {(recurringPage - 1) * PAGE_SIZE + 1}–{Math.min(recurringPage * PAGE_SIZE, filteredRecurring.length)} of {filteredRecurring.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRecurringPage((p) => p - 1)}
                      disabled={recurringPage === 1}
                    >
                      Prev
                    </Button>
                    <span className="text-xs">Page {recurringPage} of {recurringTotalPages}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRecurringPage((p) => p + 1)}
                      disabled={recurringPage === recurringTotalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* One-off expenses (not from a statement) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            One-off expenses
            <span className="ml-auto text-sm font-normal text-gray-500">
              {oneOffs.length} items · {formatINR(oneOffTotal)}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {oneOffs.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">
              No one-off expenses.{canWrite && <> Click <b>Add expense</b> above to record one.</>}
            </div>
          ) : (
            <>
              <div className="px-4 pt-3 pb-2">
                <div className="relative sm:w-80">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <Input
                    placeholder="Search expenses…"
                    value={oneOffSearch}
                    onChange={(e) => { setOneOffSearch(e.target.value); setOneOffPage(1); }}
                    className="pl-8"
                  />
                </div>
              </div>
              <div className="overflow-x-auto"><table className="w-full text-sm md:min-w-[720px]">
                <thead className="border-b">
                  <tr className="text-left text-gray-500">
                    <th className="py-2 px-3 md:px-4 font-medium w-[100px] md:w-[110px]">Date</th>
                    <th className="py-2 px-3 md:px-4 font-medium">Vendor</th>
                    <th className="py-2 px-4 font-medium hidden md:table-cell">Category</th>
                    <th className="py-2 px-4 font-medium hidden md:table-cell">Account</th>
                    <th className="py-2 px-3 md:px-4 font-medium text-right">Amount</th>
                    <th className="py-2 px-2 md:px-4 w-[80px]"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedOneOffs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-gray-400 text-sm">
                        No expenses match your search.
                      </td>
                    </tr>
                  ) : (
                    pagedOneOffs.map((e) => (
                      <tr key={e.id} className="border-b last:border-b-0 hover:bg-gray-50">
                        <td className="py-2 px-3 md:px-4 font-mono text-xs md:text-sm whitespace-nowrap">{e.payment_date || '—'}</td>
                        <td className="py-2 px-3 md:px-4 font-medium">
                          {e.vendor}
                          <span className="md:hidden block text-xs font-normal text-gray-500">{[e.category, e.payment_method].filter(Boolean).join(' · ')}</span>
                        </td>
                        <td className="py-2 px-4 text-gray-600 hidden md:table-cell">{e.category}</td>
                        <td className="py-2 px-4 text-gray-500 text-xs hidden md:table-cell">{e.payment_method}</td>
                        <td className="py-2 px-3 md:px-4 text-right font-mono whitespace-nowrap">{formatINR(e.amount)}</td>
                        <td className="py-2 px-2 md:px-4 whitespace-nowrap">
                          {canWrite && (
                            <Button variant="ghost" size="sm" className="h-9 w-9 md:h-7 md:w-7 p-0" onClick={() => openEditExpense(e)} title="Edit" aria-label="Edit expense">
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button variant="ghost" size="sm" className="h-9 w-9 md:h-7 md:w-7 p-0 text-red-700" onClick={() => deleteExpense(e.id)} title="Delete" aria-label="Delete expense">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table></div>
              {filteredOneOffs.length > PAGE_SIZE && (
                <div className="flex items-center justify-between px-4 py-3 border-t text-sm text-gray-500">
                  <span>
                    Showing {(oneOffPage - 1) * PAGE_SIZE + 1}–{Math.min(oneOffPage * PAGE_SIZE, filteredOneOffs.length)} of {filteredOneOffs.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setOneOffPage((p) => p - 1)}
                      disabled={oneOffPage === 1}
                    >
                      Prev
                    </Button>
                    <span className="text-xs">Page {oneOffPage} of {oneOffTotalPages}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setOneOffPage((p) => p + 1)}
                      disabled={oneOffPage === oneOffTotalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Past statements */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Past statements</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {statements.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">
              No statements uploaded yet.
            </div>
          ) : (
            <div className="overflow-x-auto"><table className="w-full text-sm min-w-[720px]">
              <thead className="border-b">
                <tr className="text-left text-gray-500">
                  <th className="py-3 px-4 font-medium">Period</th>
                  <th className="py-3 px-4 font-medium">Account</th>
                  <th className="py-3 px-4 font-medium">File</th>
                  <th className="py-3 px-4 font-medium text-right">Rows</th>
                  <th className="py-3 px-4 font-medium text-right">Total</th>
                  <th className="py-3 px-4 font-medium text-center">Status</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {statements.map((s) => (
                  <tr key={s.id} className="border-b last:border-b-0 hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{s.period || '—'}</td>
                    <td className="py-3 px-4">{s.account_name}</td>
                    <td className="py-3 px-4 text-gray-500 text-xs flex items-center gap-1">
                      <FileText className="w-3.5 h-3.5" />
                      {s.original_filename}
                    </td>
                    <td className="py-3 px-4 text-right font-mono">{s.expense_count}</td>
                    <td className="py-3 px-4 text-right font-mono">{formatINR(s.total_amount)}</td>
                    <td className="py-3 px-4 text-center">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs ${
                          s.status === 'finalised' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {s.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => openStatement(s)}>
                        Open
                      </Button>
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-700"
                          onClick={() => deleteStatement(s.id)}
                          title="Delete statement"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </CardContent>
      </Card>

      {/* Add Expense modal */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingExpense ? 'Edit expense' : 'Add expense'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitManual} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="exp-date">Date *</Label>
                {/* Imported recurring bills store a day like "10th"; keep those editable as text. */}
                <Input
                  id="exp-date"
                  type={editingExpense && !ISO_DATE.test(editingExpense.payment_date || '') ? 'text' : 'date'}
                  value={manualForm.payment_date}
                  onChange={(e) => setManualForm({ ...manualForm, payment_date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="exp-amount">Amount (₹) *</Label>
                <Input
                  id="exp-amount"
                  type="number"
                  step="0.01"
                  value={manualForm.amount}
                  onChange={(e) => setManualForm({ ...manualForm, amount: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Account *</Label>
              <Select
                value={manualForm.account_id}
                onValueChange={(v) => setManualForm({ ...manualForm, account_id: v })}
              >
                <SelectTrigger><SelectValue placeholder="Bank or card" /></SelectTrigger>
                <SelectContent>
                  {accounts
                    .filter((a) => a.status !== 'closed' || a.id === manualForm.account_id)
                    .map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Vendor</Label>
                <Input
                  value={manualForm.vendor}
                  onChange={(e) => setManualForm({ ...manualForm, vendor: e.target.value })}
                  placeholder="e.g. Reliance Digital"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select
                  value={manualForm.category}
                  onValueChange={(v) => setManualForm({ ...manualForm, category: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {/* Imported expenses can carry a category outside the list; keep it pickable. */}
                    {(CATEGORIES.includes(manualForm.category) ? CATEGORIES : [manualForm.category, ...CATEGORIES])
                      .map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea
                value={manualForm.description}
                onChange={(e) => setManualForm({ ...manualForm, description: e.target.value })}
                placeholder="What was this for?"
                rows={2}
              />
            </div>
            <div className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2">
              <div>
                <Label htmlFor="exp-recurring" className="text-sm font-medium">Recurring monthly</Label>
                <p className="text-xs text-gray-500">Marks as a fixed bill that hits every month</p>
              </div>
              <Switch
                id="exp-recurring"
                checked={manualForm.is_recurring}
                onCheckedChange={(v) => setManualForm({ ...manualForm, is_recurring: v })}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setManualOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={manualSaving} className="bg-black hover:bg-black/90">
                {manualSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingExpense ? 'Save changes' : 'Save expense')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
